<template>
	<section class="flex w-full flex-col rounded-lg bg-white p-2 md:p-4">
		<div
			class="flex h-10 min-h-[40px] w-full flex-shrink-0 items-center justify-between self-stretch"
		>
			<div class="flex items-center gap-2">
				<Icon :icon="['far', 'comment']" class="text-gray-700" />
				<h6 class="m-0 text-sm font-extrabold uppercase tracking-wide text-gray-900">
					{{ $t('general.team-notes') }}
				</h6>
				<span
					v-if="comments.length > 0"
					class="rounded-md bg-gray-100 px-2 py-1 text-xs"
				>
					{{ comments.length }}
				</span>
			</div>
			<Button
				v-if="!showComposer && !isEditing"
				type="tertiary"
				class="h-10 gap-0 rounded-[8px] px-[14px] text-[#303030] hover:bg-[#F5F5F5] active:bg-[#E5E5E5]"
				:aria-label="$t('general.team-notes-share-placeholder')"
				@click="openCreateComposer"
			>
				<Icon :icon="['far', 'plus']" class="text-[16px] leading-none" />
			</Button>
		</div>

		<div v-if="comments.length > 0">
			<div class="mb-1 flex items-center justify-between">
				<small class="flex items-center gap-2 text-[#171717]">
					<span class="h-2 w-2 rounded-full bg-[#171717]"></span>
					<span class="text-[14px] font-medium leading-normal text-[#171717]">
						{{ currentComment.user?.firstname }}
						{{ currentComment.user?.lastname }}
					</span>
					<span class="text-[12px] font-normal leading-normal text-[#6a7282]">
						{{ $formatDateDistance(currentComment.created_at) }}
					</span>
					<span
						v-if="isCurrentCommentEdited"
						class="text-[12px] font-normal leading-normal text-[#6a7282]"
					>
						· {{ $t('general.team-notes-edited') }}
					</span>
				</small>
				<div
					v-if="comments.length > 1 && !isEditing && !showComposer"
					class="flex items-center gap-1"
				>
					<Button
						type="tertiary"
						class="h-10 gap-0 rounded-[8px] px-[14px] text-[#303030] hover:bg-[#F5F5F5] active:bg-[#E5E5E5] disabled:opacity-50"
						:disabled="currentCommentIndex <= 0"
						@click="goToPreviousComment"
					>
						<Icon :icon="['far', 'arrow-left']" class="text-[14px] leading-none" />
					</Button>
					<small
						class="pl-1 text-[12.8px] font-normal leading-[16.64px] tracking-[0.3px] text-[#6a7282]"
					>
						{{ currentCommentIndex + 1 }}/{{ comments.length }}
					</small>
					<Button
						type="tertiary"
						class="h-10 gap-0 rounded-[8px] px-[14px] text-[#303030] hover:bg-[#F5F5F5] active:bg-[#E5E5E5] disabled:opacity-50"
						:disabled="currentCommentIndex >= comments.length - 1"
						@click="goToNextComment"
					>
						<Icon :icon="['far', 'arrow-right']" class="text-[14px] leading-none" />
					</Button>
				</div>
			</div>

			<template v-if="isEditing">
				<TipTapEditor
					:key="`edit-${editorRenderKey}`"
					:initial-content="editComment"
					:label="null"
					:show-toolbar="false"
					@editorContentUpdated="handleEditEditorUpdate"
				/>
				<div class="mt-1 flex justify-end">
					<small
						class="text-[12px]"
						:class="isEditCommentTooLong ? 'text-red-600' : 'text-[#6a7282]'"
					>
						{{ editCommentPayloadCharacterCount }}/{{ maxCommentLength }}
					</small>
				</div>
				<div
					class="mt-2 flex w-full flex-shrink-0 items-start justify-end gap-3"
					aria-label="Note edit actions"
				>
					<button
						type="button"
						class="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg border border-solid border-[#e3e3e3] bg-white text-[#303030] transition hover:bg-neutral-50 focus-visible:outline focus-visible:ring-2 focus-visible:ring-neutral-400"
						:aria-label="$t('crud.cancel')"
						@click="cancelEdit"
					>
						<IconTimes :size="16" />
					</button>
					<button
						type="button"
						class="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg border border-solid border-[#efefef] bg-[#efefef] text-[#303030] transition enabled:hover:bg-[#e5e5e5] disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline focus-visible:ring-2 focus-visible:ring-neutral-400"
						:disabled="!canSaveEdit"
						:aria-label="$t('crud.save')"
						@click="saveEdit"
					>
						<IconCheck :size="16" />
					</button>
				</div>
			</template>

			<template v-else>
				<CollapsibleRichTextNote
					:content="currentComment.comment"
					:more-label="$t('general.team-notes-show-more')"
					:less-label="$t('general.team-notes-show-less')"
				/>
				<div
					v-if="isCurrentCommentOwned && !showComposer"
					class="mt-2 flex justify-end gap-3"
				>
					<button
						type="button"
						class="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-solid border-[#efefef] bg-[#efefef] text-[#303030] transition hover:bg-[#e5e5e5] focus-visible:outline focus-visible:ring-2 focus-visible:ring-neutral-400"
						:aria-label="$t('crud.edit')"
						@click="startEdit"
					>
						<IconPencil :size="16" />
					</button>
					<button
						type="button"
						class="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-solid border-[#fdd6ce] bg-[#fdd6ce] text-[#BA0000] transition hover:bg-[#fbcabe] focus-visible:outline focus-visible:ring-2 focus-visible:ring-neutral-400"
						:aria-label="$t('general.confirm-deletion')"
						@click="deleteComment(currentComment.id)"
					>
						<IconTrash :size="18" />
					</button>
				</div>
			</template>
		</div>

		<div v-if="showComposer" class="mt-3">
			<TipTapEditor
				:key="`create-${editorRenderKey}`"
				:initial-content="newComment"
				:label="null"
				:show-toolbar="false"
				@editorContentUpdated="handleCommentEditorUpdate"
			/>
			<div class="mt-1 flex justify-end">
				<small
					class="text-[12px]"
					:class="isCommentTooLong ? 'text-red-600' : 'text-[#6a7282]'"
				>
					{{ commentPayloadCharacterCount }}/{{ maxCommentLength }}
				</small>
			</div>
			<div
				class="mt-2 flex w-full flex-shrink-0 items-start justify-end gap-3"
				aria-label="Note actions"
			>
				<button
					type="button"
					:class="[
						'inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg border border-solid transition focus-visible:outline focus-visible:ring-2 focus-visible:ring-neutral-400',
						hasCommentText
							? 'border-[#fdd6ce] bg-[#fdd6ce] text-[#BA0000] hover:bg-[#fbcabe]'
							: 'border-[#e3e3e3] bg-white text-[#6a7282] hover:bg-neutral-50'
					]"
					:aria-label="$t('general.team-notes-clear-draft')"
					@click="clearDraft"
				>
					<IconTrash
						v-if="hasCommentText"
						:size="20"
					/>
					<IconTimes
						v-else
						:size="16"
						class="text-[#303030]"
					/>
				</button>
				<button
					type="button"
					class="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg border border-solid border-[#e3e3e3] bg-[#e3e3e3] text-[#6a7282] transition enabled:hover:bg-neutral-300 disabled:cursor-not-allowed focus-visible:outline focus-visible:ring-2 focus-visible:ring-neutral-400"
					:disabled="!hasCommentText || isCommentTooLong"
					:aria-label="$t('general.team-notes-send-note')"
					@click="submitComment"
				>
					<svg
						xmlns="http://www.w3.org/2000/svg"
						width="20"
						height="20"
						viewBox="0 0 20 20"
						fill="none"
						aria-hidden="true"
					>
						<path
							d="M12.1145 18.0711C12.1461 18.15 12.2012 18.2174 12.2722 18.2641C12.3432 18.3108 12.4269 18.3347 12.5118 18.3325C12.5968 18.3303 12.6791 18.3022 12.7477 18.2519C12.8162 18.2016 12.8677 18.1316 12.8953 18.0511L18.312 2.21781C18.3386 2.14397 18.3437 2.06406 18.3266 1.98744C18.3096 1.91081 18.271 1.84064 18.2155 1.78513C18.16 1.72961 18.0898 1.69106 18.0132 1.67397C17.9366 1.65688 17.8566 1.66197 17.7828 1.68864L1.94947 7.10531C1.86905 7.13289 1.79899 7.18441 1.7487 7.25295C1.69841 7.3215 1.67028 7.40379 1.66811 7.48878C1.66593 7.57377 1.6898 7.65739 1.73652 7.72842C1.78324 7.79945 1.85057 7.85448 1.92947 7.88614L8.53781 10.5361C8.74671 10.6198 8.93652 10.7449 9.09578 10.9038C9.25504 11.0628 9.38046 11.2524 9.46447 11.4611L12.1145 18.0711Z"
							stroke="#303030"
							stroke-width="1.33333"
							stroke-linecap="round"
							stroke-linejoin="round"
						/>
						<path
							d="M18.2104 1.78906L9.09375 10.9049"
							stroke="#303030"
							stroke-width="1.33333"
							stroke-linecap="round"
							stroke-linejoin="round"
						/>
					</svg>
				</button>
			</div>
		</div>
	</section>
</template>

<script>
import Button from '~/components/ui/common/Button'
import Icon from '~/components/ui/common/Icon'
import {
	IconCheck,
	IconPencil,
	IconTimes,
	IconTrash
} from '~/components/Icons'
import TipTapEditor from '~/components/TipTapEditor'
import CollapsibleRichTextNote from '~/components/ui/notes/CollapsibleRichTextNote'
import {
	extractCommentPlainText,
	getCommentPayloadCharacterCount,
	isCommentEdited
} from '~/utils/teamNotes'

const MAX_COMMENT_LENGTH = 5000

export default {
	name: 'ApplicantProfileTeamNotes',
	components: {
		Button,
		Icon,
		IconCheck,
		IconPencil,
		IconTimes,
		IconTrash,
		TipTapEditor,
		CollapsibleRichTextNote
	},
	props: {
		application: {
			type: Object,
			default: null
		}
	},
	data() {
		return {
			comments: [],
			newComment: '',
			editComment: '',
			showCommentInput: false,
			editingCommentId: null,
			currentCommentIndex: 0,
			maxCommentLength: MAX_COMMENT_LENGTH,
			editorRenderKey: 0,
			isSavingEdit: false
		}
	},
	computed: {
		currentComment() {
			if (!this.comments.length) return null
			return this.comments[this.currentCommentIndex] || this.comments[0]
		},
		isEditing() {
			return this.editingCommentId != null
		},
		showComposer() {
			return this.showCommentInput && !this.isEditing
		},
		isCurrentCommentOwned() {
			return (
				Boolean(this.currentComment) &&
				this.currentComment.user_id === this.$user?.id
			)
		},
		isCurrentCommentEdited() {
			return isCommentEdited(this.currentComment)
		},
		hasCommentText() {
			return Boolean(this.commentPlainText.trim())
		},
		commentPlainText() {
			return extractCommentPlainText(this.newComment)
		},
		commentPayloadCharacterCount() {
			return getCommentPayloadCharacterCount(this.newComment)
		},
		isCommentTooLong() {
			return this.commentPayloadCharacterCount > this.maxCommentLength
		},
		editCommentPlainText() {
			return extractCommentPlainText(this.editComment)
		},
		editCommentPayloadCharacterCount() {
			return getCommentPayloadCharacterCount(this.editComment)
		},
		isEditCommentTooLong() {
			return this.editCommentPayloadCharacterCount > this.maxCommentLength
		},
		canSaveEdit() {
			return (
				Boolean(this.editCommentPlainText.trim()) &&
				!this.isEditCommentTooLong &&
				!this.isSavingEdit
			)
		}
	},
	watch: {
		'application.id': {
			handler() {
				this.resetState()
				this.fetchComments()
			},
			immediate: true
		}
	},
	methods: {
		resetState() {
			this.comments = []
			this.newComment = ''
			this.editComment = ''
			this.showCommentInput = false
			this.editingCommentId = null
			this.currentCommentIndex = 0
			this.isSavingEdit = false
		},
		openCreateComposer() {
			this.cancelEdit()
			this.showCommentInput = true
		},
		goToPreviousComment() {
			this.cancelEdit()
			this.currentCommentIndex--
		},
		goToNextComment() {
			this.cancelEdit()
			this.currentCommentIndex++
		},
		clearDraft() {
			if (this.hasCommentText) {
				this.newComment = ''
				this.editorRenderKey += 1
				return
			}

			this.newComment = ''
			this.showCommentInput = false
			this.editorRenderKey += 1
		},
		handleCommentEditorUpdate(content) {
			this.newComment = content || ''
		},
		handleEditEditorUpdate(content) {
			this.editComment = content || ''
		},
		startEdit() {
			if (!this.currentComment || !this.isCurrentCommentOwned) return

			this.showCommentInput = false
			this.newComment = ''
			this.editingCommentId = this.currentComment.id
			this.editComment = this.currentComment.comment || ''
			this.editorRenderKey += 1
		},
		cancelEdit() {
			this.editingCommentId = null
			this.editComment = ''
			this.isSavingEdit = false
		},
		async fetchComments() {
			if (!this.application || !this.$org) return

			try {
				const response = await this.$axios.get(
					`/application-comment/${this.application.id}`
				)
				this.comments = response.data
			} catch (error) {
				console.error('Error loading comments:', error)
			}
		},
		async submitComment() {
			if (
				!this.hasCommentText ||
				this.isCommentTooLong ||
				!this.application ||
				!this.$org
			) {
				return
			}

			try {
				const commentPayload = {
					application_id: this.application.id,
					comment: this.newComment
				}
				await this.$axios.post('/application-comment', commentPayload)
				this.newComment = ''
				this.showCommentInput = false
				this.editorRenderKey += 1
				await this.fetchComments()
				this.currentCommentIndex = 0
				this.$nextTick(() => {
					this.$emit('saved')
				})
			} catch (error) {
				console.error('Error submitting comment:', error)
			}
		},
		async saveEdit() {
			if (!this.canSaveEdit || !this.editingCommentId || !this.$org) {
				return
			}

			this.isSavingEdit = true

			try {
				const response = await this.$axios.put(
					`/application-comment/${this.editingCommentId}`,
					{ comment: this.editComment }
				)
				const updated = response.data
				const index = this.comments.findIndex(
					comment => comment.id === this.editingCommentId
				)

				if (index !== -1) {
					this.$set(this.comments, index, updated)
				} else {
					await this.fetchComments()
				}

				this.cancelEdit()
				this.$nextTick(() => {
					this.$emit('saved')
				})
			} catch (error) {
				console.error('Error updating comment:', error)
			} finally {
				this.isSavingEdit = false
			}
		},
		async deleteComment(commentId) {
			if (!confirm(this.$t('general.confirm-deletion')) || !this.$org) return

			try {
				await this.$axios.delete(`/application-comment/${commentId}`)
				this.cancelEdit()
				this.comments = this.comments.filter(
					comment => comment.id !== commentId
				)

				if (
					this.currentCommentIndex >= this.comments.length &&
					this.comments.length > 0
				) {
					this.currentCommentIndex = this.comments.length - 1
				} else if (this.comments.length === 0) {
					this.currentCommentIndex = 0
				}

				this.$emit('saved')
			} catch (error) {
				console.error('Error deleting comment:', error)
			}
		}
	}
}
</script>
